import { authFetch } from "@/lib/authFetch";
import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Target, Flame, Check, GripVertical, MoreHorizontal,
  Trash2, Trophy, Loader2, X, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import KpiCard from "@/components/dashboard/KpiCard";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Sessão UI-2 — HabitosTab
//
// Lista premium de hábitos com filtros (Todos/Hoje), 2 stats KpiCards,
// barra de progresso global, e items com check-in animado + menu (...).
//
// Honestidade sobre o brief:
//   - Day navigator com setas < > foi omitido — `checked_today` é o único
//     campo de checkin no schema; navegação por dia exigiria histórico
//     por hábito. Header mostra apenas "HOJE" + data atual em pt-BR.
//   - "Mais Feito" usa streak_current como métrica (schema não tem
//     total_checkins). Stats card mostra hábito com maior streak ativo.
//   - Drag handle (GripVertical) é VISUAL only — reorder real fica pra
//     quando o backend expor um campo `order` ou `position`.

function MicroProgressBar({ pct }) {
  return (
    <div className="h-2 bg-white/[0.04] rounded-full overflow-hidden">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
        transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
        className="h-full bg-gradient-to-r from-brand-cyan to-brand-green rounded-full"
      />
    </div>
  );
}

function HabitItem({ habit, onCheckIn, onDelete, busy }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const checked = !!habit.checked_today;
  const streak = Number(habit.streak_current || 0);
  const best = Number(habit.streak_best || 0);
  const isRecord = best > 0 && streak >= best;

  return (
    <motion.li
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -10 }}
      whileHover={{ y: -1 }}
      className={cn(
        "group relative flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-all duration-fast ease-smooth",
        "bg-surface-card hover:bg-surface-card-hover",
        checked ? "border-brand-green/30" : "border-subtle hover:border-strong",
      )}
      data-testid={`habit-item-${habit.id}`}
    >
      {/* Drag handle visual (reorder real fica pra evolução do schema) */}
      <button
        type="button"
        aria-label="Reordenar (em breve)"
        className="opacity-0 group-hover:opacity-60 transition-opacity text-foreground-subtle cursor-grab"
        tabIndex={-1}
      >
        <GripVertical className="w-4 h-4" />
      </button>

      {/* Check-in animado */}
      <motion.button
        whileTap={{ scale: 0.85 }}
        animate={checked ? { scale: [1, 1.15, 1] } : {}}
        transition={{ duration: 0.3 }}
        type="button"
        aria-label={checked ? "Hábito concluído hoje" : "Marcar concluído"}
        disabled={checked || busy}
        onClick={() => onCheckIn(habit)}
        className={cn(
          "shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-colors",
          checked
            ? "bg-brand-green/20 text-brand-green cursor-default"
            : "bg-status-warning/10 text-status-warning hover:bg-status-warning/20",
        )}
        data-testid={`habit-check-${habit.id}`}
      >
        {checked ? <Check className="w-5 h-5" /> : <Target className="w-5 h-5" />}
      </motion.button>

      {/* Conteúdo principal */}
      <div className="flex-1 min-w-0">
        <p className={cn(
          "text-sm font-medium truncate",
          checked ? "text-foreground-muted" : "text-foreground",
        )}>
          {habit.name || "(sem nome)"}
        </p>
        <p className="text-micro text-foreground-subtle mt-0.5">
          {habit.frequency === "weekly" ? "Semanal" : "Diário"}
          {habit.description ? ` · ${habit.description}` : ""}
        </p>
      </div>

      {/* Streak / record */}
      {streak > 0 && (
        <div className="shrink-0">
          {isRecord ? (
            <Badge variant="warning" data-testid={`habit-record-${habit.id}`}>
              <Trophy className="w-3 h-3 mr-1" />
              Recorde
            </Badge>
          ) : (
            <Badge variant="warning" data-testid={`habit-streak-${habit.id}`}>
              <Flame className="w-3 h-3 mr-1" />
              {streak} dia{streak === 1 ? "" : "s"}
            </Badge>
          )}
        </div>
      )}

      {/* Menu (...) — só "Excluir" funcional nesta sessão */}
      <div className="relative shrink-0">
        <button
          type="button"
          aria-label="Mais ações"
          onClick={() => setMenuOpen(o => !o)}
          className="p-1.5 rounded-md text-foreground-subtle hover:text-foreground hover:bg-surface-overlay transition-colors"
          data-testid={`habit-menu-${habit.id}`}
        >
          <MoreHorizontal className="w-4 h-4" />
        </button>
        <AnimatePresence>
          {menuOpen && (
            <>
              {/* Click-away */}
              <div
                className="fixed inset-0 z-10"
                aria-hidden="true"
                onClick={() => setMenuOpen(false)}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -4 }}
                transition={{ duration: 0.15 }}
                className="absolute right-0 top-full mt-1 z-20 min-w-[140px] bg-surface-overlay border border-subtle rounded-md shadow-xl py-1"
                role="menu"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { setMenuOpen(false); onDelete(habit); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-status-error hover:bg-status-error/10 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Excluir
                </button>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </motion.li>
  );
}

function CreateHabitModal({ open, onClose, onSubmit }) {
  const [name, setName] = useState("");
  const [frequency, setFrequency] = useState("daily");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (open) { setName(""); setFrequency("daily"); } }, [open]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit({ name: name.trim(), frequency });
      onClose();
    } finally { setSubmitting(false); }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="habit-modal-title"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-surface-overlay border border-subtle rounded-2xl p-6 safe-modal-pb w-full max-w-md"
        >
          <div className="flex items-center justify-between mb-5">
            <h3 id="habit-modal-title" className="font-display font-semibold text-h2 text-foreground">Novo hábito</h3>
            <button type="button" onClick={onClose} aria-label="Fechar" className="p-1 rounded-md text-foreground-muted hover:text-foreground hover:bg-surface-card-hover">
              <X className="w-4 h-4" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-caption text-foreground-muted mb-1.5 uppercase" style={{ letterSpacing: "0.08em" }}>Nome</label>
              <input
                autoFocus
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Treinar 30 minutos"
                className="w-full px-4 py-3 bg-surface-card border border-subtle rounded-lg text-foreground placeholder-foreground-subtle focus:outline-none focus:border-brand-cyan/50 focus:ring-1 focus:ring-brand-cyan/20 transition-colors"
              />
            </div>
            <div>
              <label className="block text-caption text-foreground-muted mb-1.5 uppercase" style={{ letterSpacing: "0.08em" }}>Frequência</label>
              <select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                className="w-full px-4 py-3 bg-surface-card border border-subtle rounded-lg text-foreground focus:outline-none focus:border-brand-cyan/50"
              >
                <option value="daily">Diário</option>
                <option value="weekly">Semanal</option>
              </select>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="ghost" onClick={onClose} className="flex-1">Cancelar</Button>
              <Button type="submit" variant="primary" disabled={!name.trim() || submitting} className="flex-1">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Criar
              </Button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function HabitosTab() {
  const { user } = useAuth();
  const userId = user?.id;
  const [habits, setHabits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // "all" | "today"
  const [modalOpen, setModalOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const fetchHabits = useCallback(async () => {
    try {
      const url = userId
        ? `${API_URL}/api/habits?user_id=${encodeURIComponent(userId)}`
        : `${API_URL}/api/habits`;
      const r = await authFetch(url);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      const list = Array.isArray(data?.habits) ? data.habits : [];
      const mine = list.filter(h =>
        h.is_active !== false && (!userId || !h.userId || h.userId === userId),
      );
      setHabits(mine);
    } catch (e) {
      console.error("HabitosTab fetch:", e);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchHabits(); }, [fetchHabits]);

  const handleCreate = async (data) => {
    try {
      const r = await authFetch(`${API_URL}/api/habits`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...data, ...(userId ? { user_id: userId } : {}) }),
      });
      if (r.ok) {
        toast.success("Hábito criado");
        await fetchHabits();
      } else {
        toast.error("Erro ao criar hábito");
      }
    } catch (_) { toast.error("Erro ao criar hábito"); }
  };

  const handleCheckIn = async (habit) => {
    if (busyId) return;
    setBusyId(habit.id);
    try {
      const r = await authFetch(`${API_URL}/api/habits/${habit.id}/checkin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (r.ok) {
        toast.success("Check-in registrado");
        await fetchHabits();
      } else {
        toast.error("Erro no check-in");
      }
    } finally { setBusyId(null); }
  };

  const handleDelete = async (habit) => {
    if (!window.confirm(`Excluir o hábito "${habit.name}"?`)) return;
    try {
      const r = await authFetch(`${API_URL}/api/habits/${habit.id}`, { method: "DELETE" });
      if (r.ok) {
        setHabits(prev => prev.filter(h => h.id !== habit.id));
        toast.success("Hábito excluído");
      }
    } catch (_) { /* ignore */ }
  };

  const todayCount = habits.filter(h => h.checked_today).length;
  const total = habits.length;
  const pct = total > 0 ? Math.round((todayCount / total) * 100) : 0;

  const visible = useMemo(() => {
    if (filter === "today") return habits.filter(h => !h.checked_today);
    return habits;
  }, [habits, filter]);

  // Stats: MAIS FEITO (maior streak_current) + MÊS ATUAL (count + label)
  const topHabit = useMemo(() => {
    if (!habits.length) return null;
    return [...habits].sort((a, b) =>
      Number(b.streak_current || 0) - Number(a.streak_current || 0)
    )[0];
  }, [habits]);

  const monthLabel = useMemo(
    () => format(new Date(), "MMMM", { locale: ptBR }).replace(/^./, c => c.toUpperCase()),
    [],
  );
  const todayLabel = useMemo(
    () => format(new Date(), "EEEE, dd 'de' MMMM", { locale: ptBR }).replace(/^./, c => c.toUpperCase()),
    [],
  );

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2" role="group" aria-label="Filtros de hábitos">
          {[
            { id: "all",   label: "Todos", count: total },
            { id: "today", label: "Hoje",  count: total - todayCount },
          ].map(f => {
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                aria-pressed={active}
                className={cn(
                  "px-3.5 py-1.5 rounded-full text-caption font-medium border transition-colors duration-fast",
                  active
                    ? "bg-brand-cyan/15 border-brand-cyan/40 text-brand-cyan"
                    : "border-subtle text-foreground-muted hover:bg-surface-card-hover",
                )}
                data-testid={`habit-filter-${f.id}`}
              >
                {f.label} <span className="opacity-70">({f.count})</span>
              </button>
            );
          })}
        </div>
        <Button
          variant="primary"
          size="md"
          onClick={() => setModalOpen(true)}
          className="bg-brand-cyan/15 border border-brand-cyan/40 text-brand-cyan hover:bg-brand-cyan/25 hover:shadow-glow-cyan"
          data-testid="habit-create-btn"
        >
          <Plus className="w-4 h-4" />
          Novo hábito
        </Button>
      </div>

      {/* Stats — 2 KpiCards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <KpiCard
          icon={Flame}
          label="Mais feito"
          tone="green"
          value={topHabit ? topHabit.name : "—"}
          valueClassName="text-[28px] md:text-[32px] leading-tight tracking-[-0.01em] font-semibold truncate"
          microcopy={topHabit
            ? `Sequência atual: ${topHabit.streak_current || 0} dia${(topHabit.streak_current || 0) === 1 ? "" : "s"}.`
            : "Sem hábitos ainda."}
          testId="habit-stat-top"
        />
        <KpiCard
          icon={Target}
          label="Mês atual"
          tone="cyan"
          value={monthLabel}
          valueClassName="text-[28px] md:text-[32px] leading-tight tracking-[-0.01em] font-semibold"
          microcopy={`${total} hábito${total === 1 ? "" : "s"} ativo${total === 1 ? "" : "s"}.`}
          testId="habit-stat-month"
        />
      </div>

      {/* Header de período + barra de progresso global */}
      <Card variant="elevated" className="glass-card glow-hover p-5 md:p-6">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div>
            <span
              className="text-caption font-semibold uppercase text-brand-cyan/80"
              style={{ letterSpacing: "0.12em" }}
            >
              Hoje
            </span>
            <p className="text-h3 text-foreground mt-1" data-testid="habits-today-label">{todayLabel}</p>
          </div>
          <button
            type="button"
            aria-label="Atualizar"
            onClick={fetchHabits}
            className="p-1.5 rounded-md text-foreground-muted hover:text-brand-cyan hover:bg-brand-cyan/10 transition-colors"
            disabled={loading}
          >
            <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
          </button>
        </div>
        <div className="flex items-center justify-between text-caption mb-2">
          <span className="text-foreground-muted">{todayCount}/{total} hábitos</span>
          <span className="text-brand-cyan font-medium tabular-nums">{pct}%</span>
        </div>
        <MicroProgressBar pct={pct} />
      </Card>

      {/* Lista */}
      {loading ? (
        <ul className="space-y-2.5" aria-label="Carregando hábitos" data-testid="habit-list-skeleton">
          {[0, 1, 2].map(i => (
            <li
              key={i}
              className="h-[68px] rounded-xl border border-subtle bg-surface-card animate-pulse"
            />
          ))}
        </ul>
      ) : habits.length === 0 ? (
        <Card variant="elevated" className="glass-card py-12 px-8 flex flex-col items-center text-center">
          <div className="w-14 h-14 rounded-full bg-status-warning/10 flex items-center justify-center mb-4">
            <Target className="w-6 h-6 text-status-warning" />
          </div>
          <h3 className="font-display font-semibold text-h2 text-foreground mb-2">Sem hábitos cadastrados</h3>
          <p className="text-body text-foreground-muted max-w-sm mb-5">
            Comece com 1 hábito pequeno e construa sua rotina sem pressa.
          </p>
          <Button variant="primary" size="md" onClick={() => setModalOpen(true)} data-testid="habit-empty-cta">
            <Plus className="w-4 h-4" />
            Criar primeiro hábito
          </Button>
        </Card>
      ) : visible.length === 0 ? (
        <Card variant="elevated" className="glass-card py-10 px-6 text-center">
          <p className="text-body text-foreground-muted">
            Tudo concluído por hoje. Bom trabalho.
          </p>
        </Card>
      ) : (
        <ul className="space-y-2.5" data-testid="habit-list">
          <AnimatePresence mode="popLayout">
            {visible.map(h => (
              <HabitItem
                key={h.id}
                habit={h}
                onCheckIn={handleCheckIn}
                onDelete={handleDelete}
                busy={busyId === h.id}
              />
            ))}
          </AnimatePresence>
        </ul>
      )}

      <CreateHabitModal open={modalOpen} onClose={() => setModalOpen(false)} onSubmit={handleCreate} />
    </div>
  );
}
